import { useEffect, useRef, useState } from 'react';
import { useStateRef } from '../../../../../../_helpers/hooks/useStateRef';
import Toast from '../../../../../../components/blackrock/advisor-center/toast/Toast';
import useToastCreate from './useToastCreate';
import {
	CloseOrigin,
	ToastData,
	ToastVisualStates,
} from '../../../../../../components/blackrock/advisor-center/toast/model';
import { ANIMATION_DELAY, DEFAULT_TOAST_LIFETIME } from './config';
import { joinString } from '../../../../../../_helpers/join-string';
import { iframeSiderailClass } from '../../../dashboard-navigation/subcomponents/Iframe/Iframe';
import { useIsDashboardNav } from '../../../dashboard-navigation/helpers/useIsDashboardNav';
import { useIsSideRailOn } from '../../../dashboard-navigation/hooks/useIsSideRailOn';
import { useEventListenerToResetToaster } from '../../_helpers/mainNavToaster.ts';

import './MainNavToaster.scss';

export const toasterId = 'blk-ac-main-nav-toaster';

const MainNavToaster: React.FC = () => {
	const toasterContainerElement = useRef<HTMLDivElement>();
	const [toastDataOrigin, resetToast] = useToastCreate();
	const [toastData, setToastData] = useState<ToastData | null>(null);
	const [visualState, visualStateRef, setVisualState] =
		useStateRef<ToastVisualStates>(ToastVisualStates.Disabled);
	const timeoutId = useRef<number>();
	const isDashboardNavigation = useIsDashboardNav();
	const isSideRailOn = useIsSideRailOn();

	// "Create Toast" event is triggered
	useEffect(() => {
		if (!toastDataOrigin) return;

		// Scenario 1:
		// Toaster will slide up
		if (visualState === ToastVisualStates.Disabled) {
			setToastData({ ...toastDataOrigin });
			return;
		}

		// Scenario 2:
		// New event triggered before the last toast could been expired
		clearTimeout(timeoutId.current);
		changeVisualState(ToastVisualStates.Closed).then(() => {
			setToastData({ ...toastDataOrigin });
		});
	}, [toastDataOrigin]);

	useEffect(() => {
		if (!toastData) return;

		changeVisualState(ToastVisualStates.Enabled).then(() => {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			if (toastData.lifetime < Infinity) {
				activateAutoclose();
			}
		});
	}, [toastData]);

	const onCloseToast = (closeOrigin: CloseOrigin) => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (toastData.onClose) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			toastData.onClose(closeOrigin);
		}

		clearTimeout(timeoutId.current);
		changeVisualState(ToastVisualStates.Closed).then(() => {
			// Close visual state has its own animation.
			// During this animation you can have a new Toast Data.
			// In this case you should break this process (close to disabled).
			if (visualStateRef.current !== ToastVisualStates.Closed) return;

			changeVisualState(ToastVisualStates.Disabled).then(() => {
				resetToaster();
			});
		});
	};

	const resetToaster = () => {
		setToastData(null);
		resetToast();
	};

	useEventListenerToResetToaster(resetToaster);

	const activateAutoclose = () => {
		window.clearTimeout(timeoutId.current);
		timeoutId.current = window.setTimeout(() => {
			changeVisualState(ToastVisualStates.Expired).then(() => {
				// Expired visual state has its own animation.
				// During this animation you can have a new Toast Data.
				// In this case you should break this process (expired to disabled).
				if (visualStateRef.current !== ToastVisualStates.Expired) return;

				changeVisualState(ToastVisualStates.Disabled).then(() => {
					resetToaster();

					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					if (toastData.onClose) {
						if (
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							toasterContainerElement.current.contains(document.activeElement)
						) {
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							toastData.onClose(CloseOrigin.LIFETIME_FOCUS_REMAINED);
						} else {
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							toastData.onClose(CloseOrigin.LIFETIME_FOCUS_HAS_LEFT);
						}
					}
				});
			});
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
		}, toastData.lifetime || DEFAULT_TOAST_LIFETIME);
	};

	const changeVisualState = (newState: ToastVisualStates): Promise<void> =>
		new Promise((resolve) => {
			window.setTimeout(() => {
				setVisualState(newState);

				if (newState === ToastVisualStates.Disabled) {
					resolve();
				} else {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					toasterContainerElement.current.addEventListener(
						'transitionend',
						() => {
							resolve();
						},
						{ once: true }
					);
				}
			}, ANIMATION_DELAY);
		});

	return (
		<div className="blk-ac-dashboard-toaster-row">
			<div className="blk-ac-dashboard-toaster" id={toasterId}>
				<div className="side-nav-toaster-wrapper">
					{isSideRailOn && isDashboardNavigation && (
						<div className="toaster-side-nav-placeholder" />
					)}

					<div
						className={joinString([
							'toaster-placeholder',
							isSideRailOn && isDashboardNavigation ? iframeSiderailClass : '',
						])}
					>
						<div
							className={['dashboard-toast-container', visualState].join(' ')}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							ref={toasterContainerElement}
						>
							{toastData !== null && (
								<div className="blk-ac-horizontal-space">
									<Toast toastData={toastData} onClose={onCloseToast} />
								</div>
							)}
						</div>
					</div>
				</div>

				{isDashboardNavigation && (
					<div className="toaster-bottom-nav-placeholder" />
				)}
			</div>
		</div>
	);
};

export default MainNavToaster;
